import { motion } from "framer-motion";
import { Flower2, Sparkles, Star, Sun } from "lucide-react";
import BrandArrow from "@/components/BrandArrow";
import neurokeyLogo from "@/assets/product-neurokey-icon.svg";
import neiromaskLogo from "@/assets/product-neiromask-icon.svg";
import relevanterLogo from "@/assets/product-relevanter-icon.svg";
import wmtKidsPhoto from "@/assets/wmt-kids-comment.jpg";
import { WMT_KIDS_URL, TRAINING_URL, IS_TRAINING_EXTERNAL } from "@/config/site";

/* ═══════════════════════════════════════════════════════════════
   «Основные направления WMT AI» — сводный блок из блюпринта.
   Сетка 2×2 из четырёх направлений; во второй карточке — под-карточки
   продуктов (Нейроключ, Релевантер). ТЁМНАЯ секция — палитра как у
   «Наших мероприятий» (EventsSection): фон #1a1a1a, карточки #222 /
   рамка #444, текст белый / #a0a0a0, коралловый акцент. Даёт тёмный
   якорь в середине страницы (второй — Мероприятия ближе к концу).
   Карточки 01/03/04 кликабельны целиком, 02 — контейнер с продуктами.
   ═══════════════════════════════════════════════════════════════ */

const easeOut = [0.22, 1, 0.36, 1] as const;

const scrollToContact = () =>
  document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });

const WMT_GROUP_URL = "https://wmtgroup.ru/";

/* Общие классы тёмной карточки (переиспользуем во всех четырёх). */
const cardBase =
  "rounded-2xl border border-[#444444] bg-[#222222] p-7 xl:p-8 transition-all duration-300 hover:bg-[#2a2a2a] hover:border-accent/50 hover:shadow-[0_16px_50px_-24px_hsl(var(--accent)/0.5)] hover:-translate-y-1";

type Product = {
  name: string;
  desc: string;
  href: string;
  logo: string;
};

const products: Product[] = [
  {
    name: "Нейроключ",
    desc: "Корпоративная среда для работы с ИИ.",
    href: "https://wmt-ai.ru/neiroklyuch/",
    logo: neurokeyLogo,
  },
  {
    name: "Нейромаск",
    desc: "Маскирование персональных данных при работе с ИИ.",
    href: "https://neiromask.vercel.app/",
    logo: neiromaskLogo,
  },
  {
    name: "Релевантер",
    desc: "ИИ-ассистент для автоматизации найма.",
    href: "https://wmt-ai.ru/relevanter/",
    logo: relevanterLogo,
  },
];

const PrimaryDirectionsSection = () => (
  <section id="directions" className="relative z-10 overflow-hidden rounded-t-[32px] bg-[#1a1a1a] py-24 sm:rounded-t-[56px] lg:py-32">
    <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {/* Заголовок */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.6, ease: easeOut }}
        className="max-w-3xl"
      >
        <h2 className="font-heading text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.12] tracking-tight text-white [text-wrap:balance]">
          Основные направления <span className="text-accent">WMT&nbsp;AI</span>
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#b0b0b0] leading-relaxed">
          Все направления связаны между собой и включаются в&nbsp;работу
          в&nbsp;зависимости от&nbsp;задачи, зрелости компании и&nbsp;типа
          инициатив. Каждое - часть масштабной AI-трансформации
        </p>
      </motion.div>

      {/* Сетка 2×2 */}
      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* 01 · Подготовка и корпоративное обучение — вся карточка кликабельна */}
        <motion.a
          href={TRAINING_URL}
          {...(IS_TRAINING_EXTERNAL ? { target: "_blank", rel: "noopener noreferrer" } : {})}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: easeOut }}
          className={`group flex w-full flex-col text-left cursor-pointer ${cardBase}`}
        >
          <span className="optical-center font-heading text-[2rem] font-semibold leading-none tabular-nums text-white/25 transition-colors group-hover:text-accent/50">
            01
          </span>
          <h3 className="mt-6 font-heading text-xl font-bold leading-snug text-white">
            Подготовка и&nbsp;корпоративное обучение
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-[#a0a0a0]">
            Стратегические сессии, интенсивы, обучение команд, аудит текущей
            ситуации и&nbsp;формирование базы инициатив. Помогает компании
            подготовиться к&nbsp;изменениям и&nbsp;создать внутреннюю мотивацию
            к&nbsp;реальному внедрению.
          </p>
          <span className="mt-auto pt-6 inline-flex items-center gap-1.5 self-start text-[13px] font-semibold leading-none text-accent transition-colors group-hover:text-accent/80">
            <span className="optical-center">Подробнее</span>
            <BrandArrow direction="up-right" className="h-2.5 w-2.5" />
          </span>
        </motion.a>

        {/* 02 · ИИ-решения в работе компании */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: easeOut, delay: 0.08 }}
          className={`flex flex-col ${cardBase}`}
        >
          <span className="optical-center font-heading text-[2rem] font-semibold leading-none tabular-nums text-white/25">
            02
          </span>
          <h3 className="mt-6 font-heading text-xl font-bold leading-snug text-white">
            ИИ-решения в&nbsp;работе компании
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-[#a0a0a0]">
            Готовые продукты и&nbsp;ИИ-агенты. Подходят для типовых задач
            в&nbsp;продажах, поддержке, маркетинге и&nbsp;HR. Их&nbsp;можно быстрее
            подключить к&nbsp;конкретному процессу.
          </p>

          {/* Под-карточки продуктов */}
          <div className="mt-6 space-y-3">
            {products.map((p) => (
              <a
                key={p.name}
                href={p.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 rounded-xl bg-white/[0.05] p-4 transition-colors hover:bg-white/[0.1]"
              >
                <img
                  src={p.logo}
                  alt={p.name}
                  className="h-11 w-11 shrink-0 object-contain"
                  loading="lazy"
                />
                <span className="min-w-0 flex-1">
                  <span className="block font-body text-[15px] font-semibold text-white">
                    {p.name}
                  </span>
                  <span className="block text-[13.5px] leading-snug text-[#a0a0a0]">
                    {p.desc}
                  </span>
                </span>
                <BrandArrow
                  direction="up-right"
                  className="h-[18px] w-[18px] shrink-0 text-accent"
                />
              </a>
            ))}
          </div>
        </motion.div>

        {/* 03 · Проектная разработка — вся карточка кликабельна */}
        <motion.button
          type="button"
          onClick={scrollToContact}
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: easeOut }}
          className={`group flex flex-col text-left cursor-pointer ${cardBase}`}
        >
          <span className="optical-center font-heading text-[2rem] font-semibold leading-none tabular-nums text-white/25 transition-colors group-hover:text-accent/50">
            03
          </span>
          <h3 className="mt-6 font-heading text-xl font-bold leading-snug text-white">
            Проектная разработка
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-[#a0a0a0]">
            Решение проектируется под внутренние данные, процессы, интеграции
            и&nbsp;требования ИТ-контура компании. Рабочие сценарии снимают часть
            регулярных операций, ускоряют подготовку решений и&nbsp;повышают
            устойчивость процессов. Режим поддержки инженеров ИИ и&nbsp;ML определяется договором.
          </p>
          <span className="mt-auto pt-6 inline-flex items-center gap-1.5 self-start text-[13px] font-semibold leading-none text-accent transition-colors group-hover:text-accent/80">
            <span className="optical-center">Подробнее</span>
            <BrandArrow className="h-3.5 w-3.5" />
          </span>
        </motion.button>

        {/* 04 · Усиление команды клиента — вся карточка кликабельна */}
        <motion.a
          href={WMT_GROUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: easeOut, delay: 0.08 }}
          className={`group flex w-full flex-col text-left cursor-pointer ${cardBase}`}
        >
          <span className="optical-center font-heading text-[2rem] font-semibold leading-none tabular-nums text-white/25 transition-colors group-hover:text-accent/50">
            04
          </span>
          <h3 className="mt-6 font-heading text-xl font-bold leading-snug text-white">
            Усиление команды клиента
          </h3>
          <p className="mt-3 text-[15px] leading-relaxed text-[#a0a0a0]">
            В&nbsp;отдельных сценариях подключаем специалистов WMT для развития
            и&nbsp;поддержки ИИ-инфраструктуры клиента в&nbsp;формате
            аутстаффинга. Это отдельное направление нашей группы компаний.
            Сокращение онбординга с&nbsp;3&nbsp;месяцев до&nbsp;2&nbsp;недель.
          </p>
          <span className="mt-auto pt-6 inline-flex items-center gap-1.5 self-start text-[13px] font-semibold leading-none text-accent transition-colors group-hover:text-accent/80">
            <span className="optical-center">Подробнее</span>
            <BrandArrow direction="up-right" className="h-2.5 w-2.5" />
          </span>
        </motion.a>

        {/* 05 · Обучение для детей — вся карточка кликабельна */}
        <motion.a
          href={WMT_KIDS_URL}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5, ease: easeOut }}
          className="group cursor-pointer overflow-hidden rounded-2xl border border-[#444444] bg-[#222222] text-white transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:bg-[#2a2a2a] hover:shadow-[0_16px_50px_-24px_hsl(var(--accent)/0.5)] lg:col-span-2"
        >
          <div className="grid min-h-[360px] md:grid-cols-[minmax(280px,42%)_1fr]">
            <div className="relative min-h-[260px] overflow-hidden md:min-h-full">
              <img
                src={wmtKidsPhoto}
                alt="Участники образовательного проекта WMT Kids"
                loading="lazy"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#222222]/20" />
            </div>

            <div className="relative flex flex-col overflow-hidden p-7 xl:p-8">
              <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <Sun className="absolute right-7 top-6 h-10 w-10 rotate-6 text-[#ffc857]/45" strokeWidth={1.35} />
                <Flower2 className="absolute bottom-7 right-10 h-8 w-8 -rotate-12 text-[#ff765b]/40" strokeWidth={1.4} />
                <Sparkles className="absolute right-24 top-24 h-6 w-6 rotate-12 text-[#77c8b2]/35" strokeWidth={1.4} />
                <Star className="absolute bottom-24 right-28 h-5 w-5 rotate-12 text-[#ffc857]/30" strokeWidth={1.4} />
              </div>
              <div className="relative z-10 flex h-full flex-col">
              <span className="optical-center font-heading text-[2rem] font-semibold leading-none tabular-nums text-white/25 transition-colors group-hover:text-accent/50">
                05
              </span>
              <h3 className="mt-6 font-heading text-2xl font-bold leading-snug">
                Обучение для детей
              </h3>
              <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-white/85">
                Практическое обучение по ИИ для школьников: развиваем цифровую
                грамотность и критическое мышление через проектные и игровые
                задания. Дети учатся формулировать задачи, работать с данными и
                собирать первые чат‑агенты и мини‑приложения под руководством
                педагогов.
              </p>

              <span className="mt-auto inline-flex items-center gap-1.5 self-start pt-7 text-[13px] font-semibold leading-none text-accent transition-colors group-hover:text-accent/80">
                <span className="optical-center">Подробнее</span>
                <BrandArrow direction="up-right" className="h-2.5 w-2.5" />
              </span>
              </div>
            </div>
          </div>
        </motion.a>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.5, ease: easeOut }}
        className="mt-8 rounded-2xl border border-[#444444] bg-[#222222] px-5 py-5 sm:px-7"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-8">
          <h3 className="shrink-0 font-heading text-xl font-bold text-white">
            Опыт и&nbsp;результаты
          </h3>
          <p className="min-w-0 flex-1 text-[15px] leading-relaxed text-[#b0b0b0]">
            WMT AI объединяет диагностику, проектирование, продукты, разработку,
            обучение и&nbsp;сопровождение в&nbsp;управляемой архитектуре внедрения.{" "}
            <strong className="font-semibold text-white">50+ проектов</strong> в&nbsp;России
            и&nbsp;СНГ, <strong className="font-semibold text-white">до&nbsp;59%</strong>{" "}
            сокращение затрат клиентов, <strong className="font-semibold text-white">до&nbsp;66%</strong>{" "}
            снижение нагрузки на&nbsp;персонал в отдельных проектах. Результат зависит от задачи, данных и условий внедрения.
          </p>
          <a
            href="/cases"
            className="wmt-cta wmt-cta--accent shrink-0 self-start px-6 text-sm lg:self-center"
          >
            <span className="optical-center">Перейти к кейсам</span>
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

export default PrimaryDirectionsSection;
